import { GEOFENCE_LIMITS, type Geofence, type Site } from "@fts/core";
import { useCallback, useEffect, useState } from "react";
import { RowAction } from "../components/RowAction";
import { Icon } from "../components/Icon";
import { useFeedback } from "../components/Toast";
import { EmptyState, Panel, Loading } from "../components/ui";
import { useI18n, type MessageKey } from "../i18n";
import { canEditGeofences, kindRule, shapeSummary, vehicleTypesSummary } from "../lib/geofences";
import { useSession, type TenantData } from "../state/session";
import { DeleteGeofenceDialog, GeofenceFormDialog } from "./GeofenceForm";

type Modal = { kind: "create" } | { kind: "edit"; zone: Geofence } | { kind: "delete"; zone: Geofence } | null;

/**
 * "Bölgeler" of a site (ADR 0015): forbidden zones, work areas and dwell limits defined on THIS site.
 * Everyone who sees the site reads them; safety officers, fleet managers and admins edit them.
 */
export function SiteGeofencesPanel({ data, site }: { data: TenantData; site: Site }) {
  const { t, locale } = useI18n();
  const { provider, tenant, role } = useSession();
  const [zones, setZones] = useState<Geofence[] | null>(null);
  const [error, setError] = useState(false);
  const [modal, setModal] = useState<Modal>(null);
  const feedback = useFeedback();
  const canEdit = canEditGeofences(role);
  const tenantId = tenant?.id;

  const reload = useCallback(async () => {
    if (!tenantId) return;
    try {
      // Only the zones defined on this site (zones of parent sites are listed on their own site page).
      setZones(await provider.listGeofences(tenantId, site.id));
      setError(false);
    } catch {
      setError(true);
      setZones((z) => z ?? []);
    }
  }, [provider, tenantId, site.id]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const full = (zones?.length ?? 0) >= GEOFENCE_LIMITS.perSite;
  const done = async (message: string) => {
    setModal(null);
    feedback.ok(message);
    await reload();
  };

  return (
    <Panel
      title={t("geofence.title")}
      meta={
        canEdit && zones !== null && !error ? (
          <button type="button" className="button button-secondary button-sm" onClick={() => setModal({ kind: "create" })} disabled={full} title={full ? t("geofence.limitReached", { max: GEOFENCE_LIMITS.perSite }) : undefined}>
            <Icon name="plus" size={15} />
            {t("geofence.add")}
          </button>
        ) : undefined
      }
      flush
    >
      <p className="panel-text zone-intro">{t("geofence.intro")}</p>
      {zones === null ? (
        <Loading />
      ) : error ? (
        <p className="form-error zone-notice" role="alert">
          {t("geofence.loadError")}
        </p>
      ) : zones.length === 0 ? (
        <EmptyState
          compact
          icon="map"
          title={t("geofence.empty")}
          action={
            canEdit ? (
              <button type="button" className="button button-secondary button-sm" onClick={() => setModal({ kind: "create" })}>
                <Icon name="plus" size={15} />
                {t("geofence.add")}
              </button>
            ) : undefined
          }
        >
          {canEdit ? t("geofence.emptyHint") : undefined}
        </EmptyState>
      ) : (
        <div className="table-wrap">
          <table className="table table-cards">
            <caption className="sr-only">{t("geofence.tableCaption", { site: site.name })}</caption>
            <thead>
              <tr>
                <th scope="col">{t("geofence.name")}</th>
                <th scope="col">{t("geofence.kind")}</th>
                <th scope="col" className="col-hide-sm">
                  {t("geofence.shape")}
                </th>
                <th scope="col" className="col-hide-sm">
                  {t("geofence.vehicleTypes")}
                </th>
                <th scope="col">{t("geofence.status")}</th>
                {canEdit && (
                  <th scope="col">
                    <span className="sr-only">{t("geofence.actions")}</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {zones.map((z) => (
                <tr key={z.id}>
                  <td className="strong cell-title" data-label={t("geofence.name")}>
                    {z.name}
                  </td>
                  <td data-label={t("geofence.kind")}>
                    <span className="zone-kind-cell">
                      <span className={`zone-swatch zone-${z.kind}`} aria-hidden="true" />
                      <span>
                        {t(`geofence.kind.${z.kind}` as MessageKey)}
                        <span className="field-hint">{kindRule(t, z)}</span>
                      </span>
                    </span>
                  </td>
                  <td className="col-hide-sm" data-label={t("geofence.shape")}>
                    {shapeSummary(t, z, locale)}
                  </td>
                  <td className="col-hide-sm" data-label={t("geofence.vehicleTypes")}>
                    {vehicleTypesSummary(t, z.vehicleTypes)}
                  </td>
                  <td data-label={t("geofence.status")}>
                    <span className={`badge ${z.active ? "tone-ok" : "tone-neutral"}`}>{z.active ? t("geofence.active") : t("geofence.inactive")}</span>
                  </td>
                  {canEdit && (
                    // "num" keeps the desktop alignment; "cell-action" moves the buttons to the card's foot on phones.
                    <td className="num cell-action">
                      <span className="row-actions">
                        <RowAction icon="edit" label={t("common.edit")} name={t("geofence.editNamed", { name: z.name })} onClick={() => setModal({ kind: "edit", zone: z })} />
                        <RowAction icon="trash" label={t("common.delete")} name={t("geofence.deleteNamed", { name: z.name })} danger onClick={() => setModal({ kind: "delete", zone: z })} />
                      </span>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!canEdit && zones !== null && zones.length > 0 && <p className="panel-footnote">{t("geofence.readOnly")}</p>}

      {(modal?.kind === "create" || modal?.kind === "edit") && (
        <GeofenceFormDialog
          site={site}
          sites={data.sites}
          zone={modal.kind === "edit" ? modal.zone : undefined}
          siteZones={zones ?? []}
          onClose={() => setModal(null)}
          onSaved={(g) => void done(t("geofence.saved", { name: g.name }))}
        />
      )}
      {modal?.kind === "delete" && <DeleteGeofenceDialog zone={modal.zone} onClose={() => setModal(null)} onDeleted={() => void done(t("geofence.deleted", { name: modal.zone.name }))} />}
    </Panel>
  );
}
